import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarDays, X } from 'lucide-react';
import { datePresets, toLocalDate } from '@/utils/datePresets';

function parseLocalDate(d) {
  if (!d) return undefined;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
}

export function DateRangePicker({ dateFrom, dateTo, onChange, presets = true }) {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [draft, setDraft] = useState({ from: undefined, to: undefined });
  const clickCount = useRef(0);

  // Reset state when popover opens
  useEffect(() => {
    if (open) {
      clickCount.current = 0;
      setStartDate(null);
      setDraft({
        from: parseLocalDate(dateFrom),
        to: parseLocalDate(dateTo),
      });
    }
  }, [open, dateFrom, dateTo]);

  const handleDayClick = (day) => {
    clickCount.current += 1;

    if (clickCount.current === 1) {
      // First click — set start date
      setStartDate(day);
      setDraft({ from: day, to: undefined });
    } else if (clickCount.current === 2) {
      // Second click — set end date and apply
      let from = startDate;
      let to = day;
      // Ensure from < to
      if (from > to) { [from, to] = [to, from]; }
      setDraft({ from, to });
      onChange(toLocalDate(from), toLocalDate(to));
      setOpen(false);
      clickCount.current = 0;
      setStartDate(null);
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange('', '');
    setOpen(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const options = presets ? datePresets() : [];
  const pick = (p) => {
    onChange(toLocalDate(p.from), toLocalDate(p.to));
    setOpen(false);
  };
  // A range that came from a preset is shown by its name - "Last 6 months" tells you more than
  // "01 May - 30 Sep" does.
  const named = options.find(p => toLocalDate(p.from) === dateFrom && toLocalDate(p.to) === dateTo);

  const calendarMonth = parseLocalDate(dateFrom) || new Date();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="date-range-picker" className="h-9 gap-2 text-xs font-medium">
          <CalendarDays size={14} strokeWidth={1.5} />
          {dateFrom && dateTo ? (
            <span>{named ? named.label : `${formatDate(dateFrom)} - ${formatDate(dateTo)}`}</span>
          ) : (
            <span className="text-muted-foreground">Date Range</span>
          )}
          {dateFrom && (
            <span role="button" tabIndex={0} onPointerDown={clear} className="ml-1 text-muted-foreground hover:text-foreground cursor-pointer" data-testid="date-range-clear">
              <X size={12} />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {presets && (
            <div className="border-r border-border p-2 flex flex-col gap-0.5 min-w-[164px]"
                 data-testid="date-presets">
              {options.map(p => (
                <button key={p.key} onClick={() => pick(p)} data-testid={`date-preset-${p.key}`}
                  className={`text-left text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                    named?.key === p.key
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'hover:bg-accent text-foreground'}`}>
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <Calendar
            mode="range"
            selected={draft}
            onDayClick={handleDayClick}
            numberOfMonths={2}
            defaultMonth={calendarMonth}
            key={open ? `open-${dateFrom}` : 'closed'}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
